import { Button, Input, Modal, Popconfirm, Space, Table, TablePaginationConfig, message } from "antd";
import { SearchOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import moment from "moment";
import OpenApi from '../../api/OpenApi';
import {
    OpenApiWrapper,
    OpenApiTitle,
    OpenApiCard,
    OpenApiInfo,
    InfoLabel,
    InfoValue,
    ToolBarWrapper,
    ToolBarLeftWrapper,
    ToolBarRightWrapper,
    TableWrapper,
    KeyDisplayWrapper,
    KeyText,
    CopyButton,
    CreateModalContent,
    FormItemLabel,
    FilterItem,
    FilterItemLabel
} from './OpenApiManagement.styled';
import StringUtils from "../../util/StringUtils";

interface OpenApiManagementProps {
}

const OpenApiManagement: React.FC<OpenApiManagementProps> = (props) => {
    // state
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [filterKeys, setFilterKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [openCreateModal, setOpenCreateKeyModal] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [newKeyDesc, setNewKeyDesc] = useState("");
    const [creating, setCreating] = useState(false);
    const [tableParams, setTableParams] = useState<{ pagination?: TablePaginationConfig }>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
        },
    });

    // api
    const openApi = new OpenApi();
    const [messageApi, contextHolder] = message.useMessage();

    // functions
    const loadApiKeys = async () => {
        setLoading(true);
        try {
            const keys = (await openApi.getAll()).data.data;
            setApiKeys(keys);
            setFilterKeys([...keys]);
        } catch (error) {
            console.error("Failed to load API keys:", error);
            messageApi.error("加载API密钥失败");
        }
        setLoading(false);
    };

    const onSearchChange = (e: any) => {
        const value = e.target.value;
        setSearch(value);

        if (!value.trim()) {
            setFilterKeys([...apiKeys]);
            return;
        }

        const filtered = apiKeys.filter(key =>
            key.name?.toLowerCase().includes(value.toLowerCase()) ||
            key.description?.toLowerCase().includes(value.toLowerCase())
        );
        setFilterKeys(filtered);
    };

    const copyToClipboard = async (openApiKey: any) => {
        try {
            await navigator.clipboard.writeText(openApiKey.token);
            messageApi.success("已复制到剪贴板");
        } catch (error) {
            console.error("Failed to copy:", error);
            messageApi.error("复制失败");
        }
    };
    
    const onCreateKey = async () => {
        if (!newKeyName.trim()) {
            messageApi.warning("请输入密钥名称");
            return;
        }

        setCreating(true);
        try {
            await openApi.create({
                name: newKeyName.trim(),
                remarks: newKeyDesc.trim()
            });
            messageApi.success("创建密钥成功");
            setOpenCreateKeyModal(false);
            setNewKeyName("");
            setNewKeyDesc("");
            loadApiKeys();
        } catch (error) {
            console.error("Failed to create key:", error);
            messageApi.error("创建密钥失败");
        }
        setCreating(false);
    };


    const onDeleteKey = async (id: string) => {
        try {
            await openApi.delete(id);
            messageApi.success("删除密钥成功");
            loadApiKeys();
        } catch (error) {
            console.error("Failed to delete key:", error);
            messageApi.error("删除密钥失败");
        }
    };

    const maskToken = (token: string) => {
        if (!token || token.length < 8) return "****";
        return token.substring(0, 4) + "****" + token.substring(token.length - 4);
    };

    const columns: any[] = [
        {
            title: '密钥名称',
            dataIndex: 'name',
            key: 'name',
            align: 'center',
            width: 180,
            render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>
        },
        {
            title: '密钥Token',
            dataIndex: 'token',
            key: 'token',
            align: 'center',
            render: (text: string, record: ApiKey) => (
                <KeyDisplayWrapper>
                    <KeyText>{maskToken(text)}</KeyText>
                    <CopyButton onClick={() => copyToClipboard(record)}>
                        <CopyOutlined />
                    </CopyButton>
                </KeyDisplayWrapper>
            )
        },
        {
            title: '创建时间',
            dataIndex: 'createdTime',
            key: 'createdTime',
            align: 'center',
            width: 180,
            render: (text: string) => (
                <span>{text ? moment(parseInt(text)).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
            )
        },
        {
            title: '过期时间',
            dataIndex: 'expiredTime',
            key: 'expiredTime',
            align: 'center',
            width: 180,
            render: (text: string) => (
                <span>{text ? moment(parseInt(text)).format('YYYY-MM-DD HH:mm:ss') : '永不过期'}</span>
            )
        },
        {
            title: '备注',
            dataIndex: 'remarks',
            key: 'remarks',
            align: 'center',
            width: 200,
            render: (text: string) => <span>{StringUtils.emptyToSlash(text)}</span>
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            width: 120,
            render: (_: any, record: ApiKey) => (
                <Space size="middle">
                    <Popconfirm
                        title="删除密钥"
                        description={<div style={{ paddingTop: "10px", paddingBottom: '10px' }}>确认要删除此密钥吗？删除后使用此密钥的外部系统将无法访问API。</div>}
                        onConfirm={() => onDeleteKey(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <a style={{ color: '#ff4d4f' }}>删除</a>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    useEffect(() => {
        loadApiKeys();
    }, []);

    return (
        <OpenApiWrapper>
            {contextHolder}

            <OpenApiCard>
                <OpenApiInfo style={{ marginBottom: 8 }}>
                    <InfoLabel>API 文档</InfoLabel>
                    <InfoValue>
                        <a href='https://winch.intcon-sys.com/swagger-ui/index.html#/' target="_blank" style={{ color: '#fff', textDecoration: 'underline' }}>
                            点击访问 Swagger API 文档
                        </a>
                    </InfoValue>
                </OpenApiInfo>
            </OpenApiCard>

            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenCreateKeyModal(true)}>
                            创建新密钥
                        </Button>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <FilterItem>
                        <FilterItemLabel>搜索</FilterItemLabel>
                        <Input
                            placeholder="搜索密钥名称或用途"
                            suffix={<SearchOutlined />}
                            value={search}
                            onChange={onSearchChange}
                        />
                    </FilterItem>
                </ToolBarRightWrapper>
            </ToolBarWrapper>

            <TableWrapper>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filterKeys}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={(pagination) => setTableParams({ pagination })}
                />
            </TableWrapper>

            <Modal
                open={openCreateModal}
                title="创建新密钥"
                onCancel={() => {
                    setOpenCreateKeyModal(false);
                    setNewKeyName("");
                    setNewKeyDesc("");
                }}
                footer={[
                    <Button key="cancel" onClick={() => setOpenCreateKeyModal(false)}>取消</Button>,
                    <Button key="create" type="primary" loading={creating} onClick={onCreateKey}>创建</Button>
                ]}
            >
                <CreateModalContent>
                    <div>
                        <FormItemLabel>密钥名称 *</FormItemLabel>
                        <Input
                            placeholder="例如：第三方系统A"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                            maxLength={50}
                        />
                    </div>
                    <div>
                        <FormItemLabel>密钥用途（可选）</FormItemLabel>
                        <Input.TextArea
                            placeholder="描述此密钥的使用场景，例如：用于XX系统访问设备数据"
                            value={newKeyDesc}
                            onChange={(e) => setNewKeyDesc(e.target.value)}
                            rows={3}
                            maxLength={200}
                        />
                    </div>
                </CreateModalContent>
            </Modal>
        </OpenApiWrapper>
    );
};

interface ApiKey {
    id: string;
    name: string;
    token: string;
    description?: string;
    expiredTime?: string;
    createTime?: string;
    status?: string;
}

export default OpenApiManagement;